// Utility function to format Firebase authentication errors into user-friendly messages
export const formatAuthError = (error) => {
  if (!error || !error.code) {
    return 'An unexpected error occurred. Please try again.';
  }

  const errorMappings = {
    'auth/email-already-in-use': 'Email Already In Use',
    'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
    'auth/invalid-email': 'Invalid email address. Did you switch to Google sign in?',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please wait a moment and try again.',
    'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
    'auth/operation-not-allowed': 'This sign-in method is not currently enabled.',
    'auth/requires-recent-login': 'Please log in again to continue with this action.',
    'auth/invalid-credential': 'Invalid credentials. Please check your email and password.',
    'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
    'auth/cancelled-popup-request': 'Sign-in request was cancelled.',
    'auth/popup-blocked': 'Sign-in popup was blocked by your browser. Please allow popups and try again.',
    'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
    'auth/invalid-verification-id': 'Invalid verification ID. Please try again.',
    'auth/missing-verification-code': 'Please enter the verification code.',
    'auth/quota-exceeded': 'Service quota exceeded. Please try again later.',
    'auth/credential-already-in-use': 'This credential is already associated with another account.',
    'auth/custom-token-mismatch': 'The custom token corresponds to a different audience.',
    'auth/invalid-custom-token': 'The custom token format is incorrect.',
    'auth/invalid-verification-code': 'The verification code is invalid.',
    'auth/missing-custom-token': 'No custom token provided.',
    'auth/missing-verification-code': 'Please provide a verification code.',
    'auth/app-deleted': 'This instance of FirebaseApp has been deleted.',
    'auth/app-not-authorized': 'This app is not authorized to use Firebase Authentication.',
    'auth/argument-error': 'An invalid argument was provided.',
    'auth/invalid-api-key': 'Invalid API key provided.',
    'auth/invalid-user-token': 'The user\'s credential is no longer valid.',
    'auth/invalid-tenant-id': 'Invalid tenant ID.',
    'auth/tenant-id-mismatch': 'The provided tenant ID does not match the Auth instance\'s tenant ID.',
    'auth/unsupported-tenant-operation': 'This operation is not supported in a multi-tenant context.',
    'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations.',
    'auth/unsupported-first-factor': 'Enrolling a second factor or signing in with a multi-factor account requires sign-in with a supported first factor.',
    'auth/unsupported-persistence-type': 'The specified persistence type is not supported on the current environment.',
    'auth/unsupported-tenant-operation': 'This operation is not supported in a multi-tenant context.',
    'auth/unverified-email': 'The email is not verified.',
    'auth/web-storage-unsupported': 'Web storage is not supported or is disabled.',
    'auth/dynamic-link-not-activated': 'Dynamic links are not activated.',
    'auth/captcha-check-failed': 'The reCAPTCHA response is invalid.',
    'auth/missing-phone-number': 'Please provide a phone number.',
    'auth/invalid-phone-number': 'Invalid phone number format.',
    'auth/missing-verification-id': 'Missing verification ID.',
    'auth/code-expired': 'The verification code has expired.',
    'auth/expired-action-code': 'The action code has expired.',
    'auth/invalid-action-code': 'The action code is invalid.',
    'auth/missing-action-code': 'Missing action code.',
    'auth/user-token-expired': 'The user\'s credential has expired.',
    'auth/invalid-recipient-email': 'Invalid recipient email.',
    'auth/invalid-sender': 'Invalid sender.',
    'auth/missing-iframe-start': 'Missing iframe start.',
    'auth/missing-ios-bundle-id': 'Missing iOS bundle ID.',
    'auth/missing-android-pkg-name': 'Missing Android package name.',
    'auth/unauthorized-continue-uri': 'Unauthorized continue URI.',
    'auth/invalid-continue-uri': 'Invalid continue URI.',
    'auth/missing-continue-uri': 'Missing continue URI.',
    'auth/missing-ios-bundle-id': 'Missing iOS bundle ID.',
    'auth/missing-android-pkg-name': 'Missing Android package name.',
    'auth/unauthorized-continue-uri': 'Unauthorized continue URI.',
    'auth/invalid-continue-uri': 'Invalid continue URI.',
    'auth/missing-continue-uri': 'Missing continue URI.',
    'auth/keychain-error': 'A keychain error occurred.',
    'auth/internal-error': 'An internal error occurred.',
    'auth/weak-password': 'Password is too weak.',
    'auth/email-already-in-use': 'Email Already In Use',
    'auth/invalid-email': 'Invalid email address.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/too-many-requests': 'Too many failed attempts.',
    'auth/network-request-failed': 'Network error.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/requires-recent-login': 'Please log in again.',
    'auth/invalid-credential': 'Invalid credentials.',
    'auth/account-exists-with-different-credential': 'Account exists with different sign-in method.',
    'auth/popup-closed-by-user': 'Sign-in cancelled.',
    'auth/cancelled-popup-request': 'Sign-in request cancelled.',
    'auth/popup-blocked': 'Sign-in popup blocked.',
    'auth/invalid-verification-code': 'Invalid verification code.',
    'auth/invalid-verification-id': 'Invalid verification ID.',
    'auth/missing-verification-code': 'Missing verification code.',
    'auth/quota-exceeded': 'Service quota exceeded.',
    'auth/credential-already-in-use': 'Credential already in use.',
    'auth/custom-token-mismatch': 'Custom token mismatch.',
    'auth/invalid-custom-token': 'Invalid custom token.',
    'auth/missing-custom-token': 'Missing custom token.',
    'auth/app-deleted': 'App deleted.',
    'auth/app-not-authorized': 'App not authorized.',
    'auth/argument-error': 'Invalid argument.',
    'auth/invalid-api-key': 'Invalid API key.',
    'auth/invalid-user-token': 'Invalid user token.',
    'auth/invalid-tenant-id': 'Invalid tenant ID.',
    'auth/tenant-id-mismatch': 'Tenant ID mismatch.',
    'auth/unauthorized-domain': 'Unauthorized domain.',
    'auth/unsupported-first-factor': 'Unsupported first factor.',
    'auth/unsupported-persistence-type': 'Unsupported persistence type.',
    'auth/unverified-email': 'Unverified email.',
    'auth/web-storage-unsupported': 'Web storage unsupported.',
    'auth/dynamic-link-not-activated': 'Dynamic links not activated.',
    'auth/captcha-check-failed': 'CAPTCHA check failed.',
    'auth/missing-phone-number': 'Missing phone number.',
    'auth/invalid-phone-number': 'Invalid phone number.',
    'auth/code-expired': 'Code expired.',
    'auth/expired-action-code': 'Expired action code.',
    'auth/invalid-action-code': 'Invalid action code.',
    'auth/missing-action-code': 'Missing action code.',
    'auth/user-token-expired': 'User token expired.',
    'auth/invalid-recipient-email': 'Invalid recipient email.',
    'auth/invalid-sender': 'Invalid sender.',
    'auth/missing-iframe-start': 'Missing iframe start.',
    'auth/missing-ios-bundle-id': 'Missing iOS bundle ID.',
    'auth/missing-android-pkg-name': 'Missing Android package name.',
    'auth/unauthorized-continue-uri': 'Unauthorized continue URI.',
    'auth/invalid-continue-uri': 'Invalid continue URI.',
    'auth/missing-continue-uri': 'Missing continue URI.',
    'auth/keychain-error': 'Keychain error.',
    'auth/internal-error': 'Internal error.'
  };

  return errorMappings[error.code] || 'An unexpected error occurred. Please try again.';
};